/**
 * event-display.ts — cómo se muestran los eventos de varios días y las
 * exposiciones (CEC-261009-8e6d).
 *
 *  - `isExposicion`: categoría con enlace `exposiciones` (la crea la líder en
 *    el admin) o, como respaldo, la Bienal por su enlace.
 *  - Rangos de fechas en hora de Colombia (`America/Bogota`), para que no
 *    dependan de la zona horaria del navegador ni del servidor de pruebas:
 *      corto → "14 – 21 OCT", "28 SEP – 2 OCT"
 *      largo → "14 al 21 de octubre de 2026",
 *              "28 de septiembre al 2 de octubre de 2026",
 *              "28 de diciembre de 2026 al 2 de enero de 2027"
 */

export const EXPOSICION_CATEGORY_SLUG = 'exposiciones';
/** Evento de la Bienal 2026 (experiencia de QR en galería): respaldo. */
export const BIENAL_QR_SLUG = 'bienal-2026';
/** Etiqueta que reemplaza al tipo de evento en las exposiciones. */
export const EXPOSICION_LABEL = 'Exposición';

const TIME_ZONE = 'America/Bogota';
const MESES_CORTOS = [
  'ENE',
  'FEB',
  'MAR',
  'ABR',
  'MAY',
  'JUN',
  'JUL',
  'AGO',
  'SEP',
  'OCT',
  'NOV',
  'DIC',
];
const MESES_LARGOS = [
  'enero',
  'febrero',
  'marzo',
  'abril',
  'mayo',
  'junio',
  'julio',
  'agosto',
  'septiembre',
  'octubre',
  'noviembre',
  'diciembre',
];

interface EventoReconocible {
  slug: string;
  category_slug?: string | null;
}

export function isExposicion(evento: EventoReconocible): boolean {
  return evento.category_slug === EXPOSICION_CATEGORY_SLUG || evento.slug === BIENAL_QR_SLUG;
}

interface Dia {
  dia: number;
  /** 0..11 */
  mes: number;
  anio: number;
}

const partesFmt = new Intl.DateTimeFormat('en-US', {
  timeZone: TIME_ZONE,
  day: 'numeric',
  month: 'numeric',
  year: 'numeric',
});

function diaEnColombia(iso: string): Dia {
  const partes = partesFmt.formatToParts(new Date(iso));
  const valor = (tipo: string) => Number(partes.find((p) => p.type === tipo)?.value);
  return { dia: valor('day'), mes: valor('month') - 1, anio: valor('year') };
}

function mismoDia(a: Dia, b: Dia): boolean {
  return a.dia === b.dia && a.mes === b.mes && a.anio === b.anio;
}

/** `true` si inicio y fin caen en días distintos (hora de Colombia). */
export function isMultiDay(startIso: string, endIso: string): boolean {
  return !mismoDia(diaEnColombia(startIso), diaEnColombia(endIso));
}

/** "14 – 21 OCT" · "28 SEP – 2 OCT". Para un solo día: "14 OCT". */
export function formatRangeShort(startIso: string, endIso: string): string {
  const a = diaEnColombia(startIso);
  const b = diaEnColombia(endIso);
  if (mismoDia(a, b)) return `${a.dia} ${MESES_CORTOS[a.mes]}`;
  if (a.mes === b.mes && a.anio === b.anio) return `${a.dia} – ${b.dia} ${MESES_CORTOS[b.mes]}`;
  return `${a.dia} ${MESES_CORTOS[a.mes]} – ${b.dia} ${MESES_CORTOS[b.mes]}`;
}

/** "14 al 21 de octubre de 2026" y variantes entre meses y entre años. */
export function formatRangeLong(startIso: string, endIso: string): string {
  const a = diaEnColombia(startIso);
  const b = diaEnColombia(endIso);
  const fin = `${b.dia} de ${MESES_LARGOS[b.mes]} de ${b.anio}`;
  if (mismoDia(a, b)) return fin;
  if (a.anio !== b.anio) return `${a.dia} de ${MESES_LARGOS[a.mes]} de ${a.anio} al ${fin}`;
  if (a.mes !== b.mes) return `${a.dia} de ${MESES_LARGOS[a.mes]} al ${fin}`;
  return `${a.dia} al ${fin}`;
}
